/* eslint-disable max-lines */
import React, { FC, memo } from 'react';
import { Typography } from 'fronton-react';
import { cx } from 'linaria';
import {
  OrderedList,
  orderedItem,
  link,
  dottedOrderedList,
  paragraph,
  paragraphHeading,
  subListItem,
  subOrderedList,
  subOrderNumber,
  subParagraph,
  dottedListItem,
  dottedListPart,
  orderNumber,
  dottedLi,
  listItem,
  number,
} from '../selling-rules.styles';
import { PHONE_NUMBER_SEO } from 'common/constants';

const phoneNumber = `tel:${PHONE_NUMBER_SEO}`;

type Props = {
  legalName: string;
};

export const Pravila: FC<Props> = memo(({ legalName }) => (
  <>
    <div style={{ margin: 'var(--space-200) 0 0 0' }}>
      <Typography variant="m" size="body_accent" color="text-primary">
        Утверждены приказом
        <br />
        Генерального директора
        <br />
        ООО &laquo;Ле Монлид&raquo;
        <br />
        № 23766 от 09.02.2026
      </Typography>
      <br />
      <Typography
        style={{ marginTop: 'var(--space-100)' }}
        variant="m"
        size="body_accent"
        color="text-primary"
      >
        Правила продажи товаров физическим лицам в интернет-магазине &laquo;{legalName}&raquo;
      </Typography>
      <Typography
        variant="m"
        size="body_accent"
        color="text-primary"
        style={{ marginTop: 'var(--space-100)' }}
      >
        Редакция от 09 февраля 2026 года
      </Typography>
      <Typography
        variant="s"
        size="body_long"
        as="p"
        style={{ margin: 'var(--space-100) 0 0 0' }}
        color="text-primary"
      >
        Настоящие Правила продажи товаров физическим лицам в Интернет-Магазине &laquo;{legalName}&raquo;
        (далее &mdash; Правила) регулируют отношения между Покупателем, ООО &laquo;ЛЕ МОНЛИД&raquo; и
        Продавцами исключительно при продаже товаров дистанционным способом с использованием
        информационно-телекоммуникационной сети &laquo;Интернет&raquo; в Интернет-Магазине &laquo;Лемана
        ПРО&raquo;, в том числе с использованием телефонной связи или мобильного приложения &laquo;Лемана
        ПРО&raquo;, а также при оказании в связи с такими продажами услуг по доставке товаров путем их
        пересылки или перевозки. Настоящие Правила не регулируют продажу товаров и услуг в Торговых центрах
        &laquo;{legalName}&raquo; и не распространяются на отношения, возникающие при продаже товаров и услуг
        в Торговых центрах &laquo;{legalName}&raquo;.
      </Typography>
    </div>
    <div>
      <OrderedList type="1">
        <li className={listItem}>
          <div className={orderedItem}>
            <Typography
              variant="m"
              size="body_accent"
              as="span"
              className={orderNumber}
              color="text-primary"
            >
              1.
            </Typography>
            <Typography variant="m" size="body_accent" className={paragraphHeading} color="text-primary">
              Термины и определения.
            </Typography>
          </div>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            Основные понятия, используемые в Правилах, означают следующее:
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Интернет-Магазин&raquo;</b> &mdash; интернет-магазин &laquo;{legalName}&raquo;,
            доступный в сети Интернет на сайте по адресу{' '}
            <a href="https://lemanapro.ru" className={link}>
              https://lemanapro.ru
            </a>{' '}
            или его поддоменах (далее &mdash; &laquo;Сайт&raquo;), а также пользователям мобильных устройств
            &mdash; в Мобильном Приложении &laquo;{legalName}&raquo;; в Интернет-Магазине представлены
            Товары, предлагаемые Покупателям для приобретения посредством оформления Заказов. В
            Интернет-Магазине продаются как Товары ООО &laquo;ЛЕ МОНЛИД&raquo;, так и Товары третьих лиц.{' '}
            {legalName} является как Продавцом собственных товаров, так и выступает платформой (агрегатором)
            для совершения сделок между Покупателями и другими Продавцами, действуя при этом по поручению
            других Продавцов и от их имени. Интернет-Магазин принадлежит ООО &laquo;ЛЕ МОНЛИД&raquo;; ООО
            &laquo;ЛЕ МОНЛИД&raquo; ведет деятельность под брендом &laquo;{legalName}&raquo;.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Мобильное приложение &laquo;{legalName}&raquo;&raquo;</b> &mdash; программное
            обеспечение, предназначенное для работы на смартфонах, планшетах, и др. мобильных устройствах под
            управлением мобильной операционной системы IOS, Android и др., принадлежащее ООО &laquo;ЛЕ
            МОНЛИД&raquo; и предназначенное для поиска, выбора и заказа Товаров в Интернет-магазине.
            Мобильное приложение &laquo;{legalName}&raquo; доступно владельцам мобильных устройств для
            бесплатного скачивания и установки в магазинах мобильных приложений третьих лиц.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Товар&raquo;</b> &mdash; объект материального мира, не изъятый из гражданского оборота
            и представленный к продаже в Интернет-Магазине, и имеющийся в наличии на момент Подтверждения
            готовности Товара к отгрузке. Товары, помеченные ярлычком &laquo;Только онлайн-заказ&raquo;, не
            продаются в Торговых центрах &laquo;{legalName}&raquo;, их можно купить только в
            Интернет-магазине.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Продавец&raquo;</b> &mdash; ООО &laquo;ЛЕ МОНЛИД&raquo; ({legalName}) либо иное
            юридическое лицо или индивидуальный предприниматель, указанные в качестве Продавца на странице
            Товара. Информация о {legalName} приведена на страничке Сайта &laquo;Контакты&raquo;. Информация
            об ином Продавце (фирменное наименование, место нахождения, режим работы, государственный
            регистрационный номер записи о создании юридического лица, фамилия, имя, отчество,
            государственный регистрационный номер записи о государственной регистрации физического лица в
            качестве индивидуального предпринимателя) размещена в Интернет-Магазине в карточке такого Товара
            рядом с ярлычком &laquo;Только онлайн-заказ&raquo; под знаком &laquo;(i)&raquo; (строчная
            латинская буква &laquo;i&raquo; в кружочке), а в кассовых чеках такой Продавец называется
            &laquo;поставщиком&raquo;.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Покупатель&raquo;</b> &mdash; любое полностью дееспособное физическое лицо,
            заказывающее, приобретающее или использующее Товары исключительно для личных, семейных, домашних
            и иных нужд, не связанных с осуществлением предпринимательской деятельности, либо лицо, указанное
            в качестве Получателя приобретаемого Товара.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Получатель&raquo;</b> &mdash; Покупатель либо лицо, которое уполномочено Покупателем на
            получение заказанного Товара от имени Покупателя и (или) приемку Дополнительных услуг, и данные
            которого Покупатель сообщает {legalName} при оформлении Заказа.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Заказ&raquo;</b> &mdash; оформленный в Интернет-Магазине через Сайт и/или с
            использованием Мобильного приложения &laquo;{legalName}&raquo; либо с использованием телефонной
            связи запрос Покупателя на получение по указанному им адресу перечня Товаров, выбранных на Сайте.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Личный кабинет&raquo;</b> &mdash; персональная страница Покупателя на Сайте и в
            Мобильном приложении &laquo;{legalName}&raquo;, для доступа к которой необходимы логин и пароль.
            В Личном кабинете хранится, в частности, информация о Покупателе и его Заказах.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Подтверждение получения Заказа&raquo;</b> &mdash; автоматическое направление Покупателю
            системой Сайта информации о получении Заказа Покупателя.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Подтверждение о готовности товара к отгрузке&raquo;</b> &mdash; направление {legalName}
            Покупателю информации о том, что Заказ собран и готов к выдаче в Торговом центре &laquo;Лемана
            ПРО&raquo; либо собран и передан Продавцом Службе доставки для доставки Получателю. Обязательство
            Продавца по передаче Покупателю Товара возникает с момента направления {legalName} Покупателю
            подтверждения о готовности Товара к отгрузке. Если подтверждение о готовности Товара к отгрузке
            не направлено Покупателю, условие признается не наступившим, а договор с Покупателем &mdash; не
            заключенным.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Служба доставки&raquo;</b> &mdash; третье лицо, оказывающее по договору с Продавцом
            услуги по доставке Заказов Получателям.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Дополнительные услуги&raquo;</b> &mdash; оказываемые Продавцом в связи с продажей
            Товара, включая, но не ограничиваясь, услуги по доставке Товаров путем их пересылки почтовыми
            отправлениями или перевозки.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={paragraph} color="text-primary">
            <b>&laquo;Рекомендательные технологии&raquo;</b> &mdash; информационные технологии предоставления
            информации на основе сбора, систематизации и анализа сведений, относящихся к предпочтениям
            пользователей сети &laquo;Интернет&raquo;, находящихся на территории Российской Федерации.
            Применяются на Сайте и в Мобильном приложении. Правила и информация о применении рекомендательных
            технологий изложены на отдельной странице Сайта{' '}
            <a
              href="https://lemanapro.ru/legal/pravila-primeneniya-rekomendatelnyh-tehnologiy/"
              className={link}
            >
              https://lemanapro.ru/legal/pravila-primeneniya-rekomendatelnyh-tehnologiy/.
            </a>
          </Typography>
        </li>
        <li className={listItem}>
          <div className={orderedItem}>
            <Typography
              variant="m"
              size="body_accent"
              as="span"
              className={orderNumber}
              color="text-primary"
            >
              2.
            </Typography>
            <Typography variant="m" size="body_accent" className={paragraphHeading} color="text-primary">
              Общие положения о продаже Товаров
            </Typography>
          </div>
          <ol className={subOrderedList}>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                2.1.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Настоящие Правила, постоянно размещенные в сети &laquo;Интернет&raquo; на Сайте и в Мобильном
                приложении, в соответствии со ст. 435 и ч.2 ст. 437 ГК РФ являются публичной офертой.
                Конкретные тематические разделы и страницы Сайта с описанием Товаров, с информацией об
                условиях их оплаты и доставки (в том числе Правила доставки на странице{' '}
                <a href="/service/dostavka" className={link}>
                  https://lemanapro.ru/service/dostavka
                </a>
                ), с информацией о Продавцах, с информацией об условиях возврата Товаров (в том числе условия
                возврата на странице{' '}
                <a href="/service/vozvrat-tovara/" className={link}>
                  https://lemanapro.ru/service/vozvrat-tovara/
                </a>
                ) являются неотъемлемой частью настоящих Правил.
                <Typography
                  variant="s"
                  size="body_long"
                  as="p"
                  style={{ margin: 'var(--space-100) 0 0 0' }}
                  color="text-primary"
                >
                  Настоящая оферта адресована только покупателям-физическим лицам. Условия настоящих Правил
                  не распространяются на покупки Товаров в Интернет-Магазине юридическими лицами,
                  индивидуальными предпринимателями и участниками программы лояльности &laquo;Ключ-карта
                  ПРО&raquo; (ранее &laquo;Профессиональная карта&raquo;). Правила продажи Товаров
                  юридическим лицам и индивидуальным предпринимателям, Правила программы лояльности
                  &laquo;Ключ-карта ПРО&raquo; размещены на Сайте отдельно.
                </Typography>
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                2.2.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В соответствии со статьей 438 ГК РФ выражение Покупателем согласия с условиями оферты путём
                нажатия кнопки &laquo;Оформить заказ&raquo;/&laquo;Перейти к оплате&raquo; при оформлении
                Заказа является акцептом Покупателем настоящей оферты. Нажимая на кнопку &laquo;Оформить
                заказ&raquo;/&laquo;Перейти к оплате&raquo; при оформлении Заказа на Сайте либо давая устное
                согласие оператору при оформлении Заказа по телефону, Покупатель подтверждает свое
                ознакомление и согласие с настоящими Правилами и сообщает Продавцу о своём намерении
                заключить договор розничной купли-продажи на условиях настоящей оферты. Правила доставки,
                опубликованные на странице{' '}
                <a href="/service/dostavka" className={link}>
                  https://lemanapro.ru/service/dostavka
                </a>
                , содержат информацию об условиях оплаты и доставки Товаров и являются неотъемлемой частью
                настоящей оферты. Условия возврата Товаров, опубликованные на странице{' '}
                <a href="/service/vozvrat-tovara/" className={link}>
                  https://lemanapro.ru/service/vozvrat-tovara/
                </a>
                {/* */}, также являются неотъемлемой частью настоящей оферты.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                2.3.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                При оплате Покупателем &laquo;Картой на сайте&raquo; (перечисления аванса в счет будущей
                сделки) договор считается заключенным с момента направления потребителю кассового чека,
                подтверждающего оплату (полный расчет).
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                2.4.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Покупатель гарантирует, что товары приобретаются им для личных, семейных, домашних и иных
                нужд, не связанных с осуществлением предпринимательской деятельности. Покупатель также
                гарантирует, что ему не менее 18 лет, а если ему меньше 18 лет, то право на заказ Товаров
                предоставлено ему по закону и он обладает дееспособностью для акцепта настоящей оферты.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                2.5.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В Интернет-Магазине продаются Товары как из собственного ассортимента {legalName}, так и
                Товары иных юридических лиц или индивидуальных предпринимателей, указанных в качестве
                Продавца на странице Товара рядом с ярлычком &laquo;Только онлайн-заказ&raquo; &mdash; под
                значком &laquo;(i)&raquo;. При заказе Товары каждого Продавца группируются отдельно, оплата
                за каждый такой взимается отдельно. {legalName} не является продавцом Товара по договорам
                купли-продажи Товара, в карточке которого рядом с ярлычком &laquo;Только онлайн-заказ&raquo;
                под значком &laquo;(i)&raquo; приведена информация о Продавце. Все взаимоотношения, связанные
                с куплей-продажей Товара в таком случае, возникают непосредственно между соответствующим
                Продавцом и Покупателем, и такой Продавец несет ответственность перед Покупателем в
                соответствии с действующим законодательством Российской Федерации. {legalName} не является
                уполномоченным лицом других Продавцов в смысле законодательства о защите прав потребителей.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                2.6.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                {legalName} оставляет за собой право в одностороннем порядке вносить изменения в настоящие
                Правила, в связи с чем Покупателю рекомендуется знакомиться с Правилами при каждом оформлении
                Заказа.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                2.7.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Не все Товары, представленные в Интернет-Магазине, предназначены для продажи дистанционным
                способом. Если продажа Товара в Интернет-Магазине подразумевает предварительное согласование
                Продавцом условий договора розничной купли-продажи, в том числе согласование наличия,
                наименования и количества Товара, а также в случаях, когда Продавец явно указал в описании
                Товара, что соответствующий Товар не предназначен для продажи дистанционным способом.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                2.8.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Товары, помеченные в Интернет-Магазине ярлычком &laquo;Есть образец. Приезжайте посмотреть и
                заказать&raquo;, не продаются в Интернет-Магазине. Договор розничной купли-продажи такого
                Товара может быть заключен только в Торговом центре &laquo;{legalName}&raquo; на основании
                ознакомления Покупателя с выставленным там образцом товара.
              </Typography>
            </li>
          </ol>
        </li>
        <li className={listItem}>
          <div className={orderedItem}>
            <Typography
              variant="m"
              size="body_accent"
              as="span"
              className={orderNumber}
              color="text-primary"
            >
              3.
            </Typography>
            <Typography variant="m" size="body_accent" className={paragraphHeading} color="text-primary">
              Оформление Заказа
            </Typography>
          </div>
          <ol className={subOrderedList}>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                3.1.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Заказ может быть принят оператором {legalName} по телефону, указанному в Интернет-Магазине,
                или оформлен Покупателем самостоятельно на Сайте или в Мобильном приложении &laquo;Лемана
                ПРО&raquo;. Для оформления Заказа необходимо указать номер телефона.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                3.2.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Заказ можно оформить и без регистрации в Интернет-Магазине. Однако для оперативного
                оформления последующих Заказов Покупателю рекомендуется зарегистрироваться в
                Интернет-Магазине и создать Личный кабинет. Для создания Личного кабинета Покупателю
                необходимо указать в Интернет-Магазине свой номер мобильного телефона, подтвердив действие с
                помощью ввода поступившего одноразового кода из SMS. Для входа в Личный кабинет можно
                использовать телефон или адрес электронной почты (если он был ранее указан в Личном
                кабинете).
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                3.3.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                {legalName} не несет ответственности за содержание и достоверность информации,
                предоставляемой Покупателем при оформлении Заказов и при создании Личного кабинета.
                Покупатель несет риски невозможности получения Заказа и/или невозможности получения
                уведомлений по Заказу и/или невозможности получения денежных средств при возврате Заказа по
                причине предоставления им недостоверной/некорректной информации и/или контактных данных,
                предоставления контактных данных третьих лиц (в том числе при оформлении Заказа или при
                создании Личного кабинета).
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                3.4.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Покупатель обязуется не сообщать третьим лицам получаемые им от {legalName} и служб доставки
                одноразовые коды (необходимые для доступа к Личному кабинету, для оформления Заказа и т.п.).
                Все действия, совершенные под учетной записью Покупателя (с использованием его логина и
                пароля), считаются произведенными самим Покупателем, за исключением случаев, когда Покупатель
                уведомил {legalName} о несанкционированном доступе к его логину, паролю и одноразовым кодам.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                3.5.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В случае возникновения у Покупателя подозрений о возможности несанкционированного
                использования его учётной записи третьими лицами, Покупатель обязуется незамедлительно
                уведомить об этом {legalName}, направив электронное письмо через форму обратной связи по
                ссылке{' '}
                <a href="/company/obratnaya-svyaz/" className={link}>
                  https://lemanapro.ru/company/obratnaya-svyaz/
                </a>{' '}
                на Сайте или по телефону{' '}
                <a href={`tel:${PHONE_NUMBER_SEO}`} className={cx(link, number)}>
                  8 (800) 700-00-99
                </a>
                . {legalName}, в свою очередь, обязуется соблюдать конфиденциальность направляемых им
                Покупателю кодов, и требовать соблюдения конфиденциальности Службами доставки и Продавцами.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                3.6.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                При оформлении Заказа по телефону Покупатель сообщает оператору {legalName} желаемые артикулы
                Товара. Оператор проверят наличие и цену данных Товаров, согласовывает с Покупателем условия
                доставки и получает подтверждение Заказа у Покупателя. Затем {legalName} направляет
                Покупателю Подтверждение получения Заказа, содержащее уведомление о необходимости оплатить
                Заказ и ссылку на оплату.
                <Typography
                  variant="s"
                  size="body_long"
                  as="p"
                  style={{ margin: 'var(--space-100) 0 0 0' }}
                  color="text-primary"
                >
                  После получения {legalName} Заказа, сделанного Покупателем самостоятельно (на Сайте или в
                  Мобильном приложении &laquo;{legalName}&raquo;), если Товары не были оплачены
                  непосредственно при оформлении Заказа, {legalName} направляет Покупателю автоматическое
                  Подтверждение получения Заказа и уведомление о необходимости оплатить заказанные Товары.
                </Typography>
                <Typography
                  variant="s"
                  size="body_long"
                  as="p"
                  style={{ margin: 'var(--space-100) 0 0 0' }}
                  color="text-primary"
                >
                  После нажатия Покупателем кнопки &laquo;Оплатить&raquo; на платежной странице или в
                  приложении Банка, {legalName} направляет Покупателю автоматическое подтверждение принятия
                  заказа. После обработки Заказа Продавцом и передачи Товаров Службе доставки для передачи
                  Товаров Покупателю, {legalName} направляет Покупателю автоматическое Подтверждение
                  готовности Товаров к отгрузке с указанием даты доставки Товаров. Сообщения с информацией о
                  текущем статусе Заказа Покупателю также может направлять Продавец и Служба доставки.
                </Typography>
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                3.7.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Дата доставки Товаров Покупателем зависит от адреса места нахождения Продавца, адреса и
                региона доставки, и от выбранного Покупателем способа доставки Товаров.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                3.8.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В силу особенности организации торговли дистанционным способом в Интернет-Магазине, Продавец
                не гарантирует наличие заказанных Товаров на складе на момент сборки Заказа. Если Товар был в
                наличии в момент оформления Заказа, но на момент сборки Заказа на складе Продавца Товар уже
                закончился (либо отсутствовало необходимое количество заказанного Товара), {legalName}
                информирует об этом Покупателя. Обязанность Продавца передать Покупателю Товар возникает
                только с момента направления {legalName} Покупателю Подтверждения о готовности Товара к
                отгрузке.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                3.9.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В случае отсутствия Товара на складе Продавца, по согласованию с Покупателем, либо срок
                доставки Товаров может быть увеличен на срок не более 30 (Тридцать) календарных дней, либо
                Заказ может быть отменен. Если Товар отсутствует частично (и если Покупатель сообщит Лемана
                ПРО в течение 2-х дней о своем согласии принять Товар в количестве, имеющемся в наличии у
                Продавца), то Продавец отправляет Покупателю Товары в согласованном с Покупателем количестве;
                отказ Покупателя принять Товар в согласованном количестве может считаться отказом Покупателя
                от Заказа целиком.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                3.10.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                {legalName} может связаться с Покупателем, не завершившим оформление Заказа, для уточнения
                информации и, при необходимости, оказания Покупателю помощи в оформлении Заказа.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                3.11.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Продавец вправе отказать в оформлении Заказа, если у Покупателя на момент оформления Заказа
                оформлены, но еще не получены 10 (десять) и более других Заказов.
              </Typography>
            </li>
          </ol>
        </li>
        <li className={listItem}>
          <div className={orderedItem}>
            <Typography
              variant="m"
              size="body_accent"
              as="span"
              className={orderNumber}
              color="text-primary"
            >
              4.
            </Typography>
            <Typography variant="m" size="body_accent" className={paragraphHeading} color="text-primary">
              Информация о Товаре
            </Typography>
          </div>
          <ol className={subOrderedList}>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                4.1.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В силу объективных причин (особенности индивидуального восприятия человеком, особенности
                цветопередачи монитора компьютера, технические ошибки) описания могут не в полной мере
                передавать достоверную информацию о свойствах и характеристиках Товара, включая цвет, размер,
                форму. В случае возникновения у Покупателя вопросов, касающихся свойств и характеристик
                Товара, перед оформлением Заказа Покупатель вправе обратиться за консультацией через форму
                обратной связи на Сайте{' '}
                <a href="/company/obratnaya-svyaz/" className={link}>
                  https://lemanapro.ru/company/obratnaya-svyaz/
                </a>{' '}
                или по телефону{' '}
                <a href={phoneNumber} className={cx(link, number)}>
                  8 (800) 700-00-99
                </a>
                .
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                4.2.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Информация о Товаре, включая условия его эксплуатации и правила хранения, доводится до
                Покупателя путем размещения в Интернет-Магазине, на Товаре, на электронных носителях,
                прикладываемых к Товару, в самом Товаре (на электронной плате внутри Товара в разделе меню),
                на таре, упаковке, ярлыке, этикетке, в технической документации или иным способом,
                установленным законом. Обязанность Продавца, предусмотренная пунктом 3 статьи 26.1 Закона
                Российской Федерации &laquo;О защите прав потребителей&raquo;, признается исполненной также в
                случае предоставления Покупателю информации с помощью электронных и иных технических средств.
              </Typography>
            </li>
          </ol>
        </li>
        <li className={listItem}>
          <div className={orderedItem}>
            <Typography
              variant="m"
              size="body_accent"
              as="span"
              className={orderNumber}
              color="text-primary"
            >
              5.
            </Typography>
            <Typography variant="m" size="body_accent" className={paragraphHeading} color="text-primary">
              Передача Товаров Покупателю
            </Typography>
          </div>
          <ol className={subOrderedList}>
            <li className={cx(subListItem, dottedListItem)}>
              <div className={dottedListPart}>
                <Typography
                  variant="s"
                  size="body_long"
                  as="span"
                  className={subOrderNumber}
                  color="text-primary"
                >
                  5.1.
                </Typography>
                <Typography
                  variant="s"
                  size="body_long"
                  as="p"
                  className={subParagraph}
                  color="text-primary"
                >
                  Товары Покупателю доставляет привлеченная {legalName} Служба доставки или лицо, указанное в
                  качестве Продавца на странице Товара в Интернет-Магазине. Подробная информация об услугах
                  доставки, о порядке заказа и оплаты услуг доставки и об условиях доставки Товаров приведена
                  в Правилах доставки, опубликованных на странице{' '}
                  <a href="/service/dostavka" className={link}>
                    https://lemanapro.ru/service/dostavka
                  </a>
                  , которые являются неотъемлемой частью настоящих Правил.
                  <Typography
                    variant="s"
                    size="body_long"
                    as="p"
                    style={{ margin: 'var(--space-100) 0 0 0' }}
                    color="text-primary"
                  >
                    В случае отсутствия всех заказанных Товаров в одном подразделении {legalName} или у
                    одного Продавца, Заказ Покупателя будет разбит на несколько отправлений из разных
                    подразделений
                    {legalName}/Продавцов, соответственно. Доставка каждого такого отправления осуществляется
                    из разных подразделений {legalName} и/или от разных Продавцов; срок доставки каждого
                    такого отправления Клиент указывает при оформлении Заказа отдельно; плата за доставку
                    каждого такого отправления взимается отдельно.
                  </Typography>
                  <Typography
                    variant="s"
                    size="body_long"
                    as="p"
                    style={{ margin: 'var(--space-100) 0 0 0' }}
                    color="text-primary"
                  >
                    Передача Товаров Покупателю осуществляется способом, выбранным Покупателем при оформлении
                    Заказа:
                  </Typography>
                </Typography>
              </div>
              <ol className={dottedOrderedList}>
                <li className={dottedLi}>
                  <Typography
                    variant="s"
                    size="body_long"
                    as="p"
                    className={subParagraph}
                    color="text-primary"
                  >
                    транспортной компанией по адресу, указанному Покупателем (только для Товаров из
                    ассортимента Торгового центра &laquo;{legalName}&raquo;),
                  </Typography>
                </li>
                <li className={dottedLi}>
                  <Typography
                    variant="s"
                    size="body_long"
                    as="p"
                    className={subParagraph}
                    color="text-primary"
                  >
                    курьером Службы доставки по адресу, указанному Покупателем,
                  </Typography>
                </li>
                <li className={dottedLi}>
                  <Typography
                    variant="s"
                    size="body_long"
                    as="p"
                    className={subParagraph}
                    color="text-primary"
                  >
                    в выбранном Покупателем пункте выдачи или Постамате Службы доставки,
                  </Typography>
                </li>
                <li className={dottedLi}>
                  <Typography
                    variant="s"
                    size="body_long"
                    as="p"
                    className={subParagraph}
                    color="text-primary"
                  >
                    самовывозом из выбранного Покупателем Пункта выдачи или Постамата Торгового центра
                    &laquo;{legalName}&raquo;.
                  </Typography>
                </li>
              </ol>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.2.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Доступность каждого конкретного способа передачи Товара Клиенту определяется при оформлении
                Заказа. В зависимости от выбранного Товара, способа заказа (на Сайте, по телефону, через
                Мобильное приложение &laquo;{legalName}&raquo;), региона и адреса доставки могут быть
                доступны только определенные способы и услуги доставки. Согласованным способом доставки
                считается способ, выбранный Покупателем из числа доступных при оформлении Заказа. {legalName}{' '}
                не предлагает услугу доставки некоторых Товаров (такие Товары можно забрать только
                самовывозом из выбранного Покупателем Торгового центра &laquo;{legalName}&raquo;. Услуга
                доставки курьером или в пункт выдачи/в Постамат предоставляется не на всей территории
                Российской Федерации. Выдача Товаров в Пункте выдачи или Постамате осуществляется в рабочие
                часы соответствующего Пункта выдачи или Торгового центра &laquo;{legalName}&raquo;. С
                информацией о графике работы Торговых центров &laquo;{legalName}&raquo; можно ознакомиться на
                Сайте; о графике работы Пунктов выдачи Служб доставки можно узнать в соответствующей Службе
                доставки. В случае невозможности доставки Товара, {legalName} уведомляет Покупателя об этом
                при оформлении Заказа. Подробные Правила доставки приведены на Сайте на странице{' '}
                <a href="/service/dostavka" className={link}>
                  https://lemanapro.ru/service/dostavka/
                </a>
                .
                <Typography
                  variant="s"
                  size="body_long"
                  as="p"
                  style={{ margin: 'var(--space-100) 0 0 0' }}
                  color="text-primary"
                >
                  Если вес Заказа превышает 30 (Тридцать) кг, занос Заказа курьером в дом/квартиру и подъём
                  на этаж доступны не во всех населенных пунктах; возможность оказания данных услуг будет
                  определена после оформления Заказа, в зависимости от населенного пункта получения Заказа.
                </Typography>
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.3.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Дату и временной интервал получения Товаров Покупатель выбирает при оформлении Заказа из
                предложенных в Интернет-Магазине вариантов .
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.4.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В случае предоставления Покупателем неверных контактных данных (своих или третьих лиц, в том
                числе предоставление данных, не принадлежащих Покупателю и/или Получателю) {legalName} и/или
                Продавец не несут ответственности за ненадлежащее исполнение Заказа.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.5.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                По факту поступления Товаров в Пункт выдачи заказов, в выбранный Покупателем торговый центр
                &laquo;{legalName}&raquo; или при передаче Заказа курьеру, Покупателю будет направлено
                сообщение. Только после получения данного сообщения Покупателем Заказ можно забрать в Пункте
                выдачи заказов, в Постамате или в Торговом центре &laquo;{legalName}&raquo;. Покупатель
                обязуется не сообщать третьим лицам (за исключением Получателя) сведения, необходимые для
                получения Товаров. Сообщения с информацией о текущем статусе Заказа Покупателю также может
                направлять Продавец и Служба доставки. Продавец может звонить Клиенту в день доставки Заказа
                и после получения Заказа с целью подтверждения соблюдения условий доставки.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.6.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Если при оформлении Заказа не был указан иной срок, то Товары можно забрать в Пункте выдачи
                заказов в течение 7 (Семь) календарных дней, а в Торговом центре &laquo;{legalName}&raquo;
                или Постамате &mdash; в течение 3 (Три) календарных дней со дня поступления Товаров в Пункт
                выдачи заказов или в Торговый центр, соответственно. Стоимость доставки и сроки хранения
                Товаров в Постаматах, Пунктах выдачи заказов и Торговых центрах &laquo;{legalName}&raquo;
                Покупатель может уточнить на Сайте до оформления Заказа. Товар может быть доставлен в
                Постамат или в Пункт выдачи заказов ранее обещанной даты, о чем {legalName} уведомит
                Покупателя; в таком случае срок, отведенный на получение Товаров, отсчитывается со дня
                фактической доставки Товара в Постамат или в Пункт выдачи заказов.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.7.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Доставленные в Пункт выдачи заказов, Постамат или в Торговый центр &laquo;{legalName}&raquo;
                Товары Покупатель должен получить в указанный в уведомлении срок, а если Покупатель пропустил
                или не получил уведомление, то он сам должен предпринять в разумный срок необходимые и
                достаточные действия для того, чтобы получить от {legalName} информацию о сроке поступления
                Товаров в Пункт выдачи заказов или в выбранный Покупателем Торговый центр &laquo;Лемана
                ПРО&raquo;. Неявка Получателя за Товарами в указанный в уведомлении/сообщенный Покупателю
                срок является основанием для отказа Продавца от договора купли-продажи (ст. 484 ГК РФ) и для
                отмены Заказа. Если неполученные Товары были предоплачены, денежные средства возвращаются
                Покупателю в порядке, предусмотренном настоящими Правилами.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.8.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Если Товары были доставлены в установленный срок, но не были получены Покупателем по его
                вине, последующая доставка (если Заказ не расформирован и повторная доставка возможна)
                производится в новые сроки, согласованные с {legalName}, после повторной оплаты Покупателем
                стоимости доставки (при заказе Покупателем Товаров из ассортимента {legalName} и выборе
                Покупателем доставки силами транспортной компании из Торгового центра &laquo;Лемана
                ПРО&raquo;).
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.9.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                {legalName} приложит все усилия для соблюдения сроков доставки, указанных при оформлении
                Заказа, тем не менее, задержки в доставке или доставка раньше намеченного срока возможны
                ввиду непредвиденных обстоятельств, произошедших не по вине {legalName}.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.10.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Товары вручаются Покупателю или Получателю, а в их отсутствие &mdash; любому лицу,
                предъявившему информацию о номере Заказа, либо иное (в том числе электронное) подтверждение
                заключения договора розничной купли-продажи или оформления Заказа Покупателем. Товары
                вручаются лицу, готовому предоставить сведения о Заказе (номер Заказа и/или ФИО Получателя,
                Покупателя), а также оплатить лицу, осуществляющему доставку Товаров, стоимость Товаров в
                полном объеме. Для получения Товаров в Постамате Покупатель должен ввести ПИН-код. Лицо,
                которое ввело ПИН-код или предоставило сведения о Заказе, считается уполномоченным
                Получателем Товара.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.11.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В случае, если Покупатель передал третьему лицу номер Заказа (сообщил ПИН-код или предоставил
                иные сведения о Заказе) и такое лицо сообщает данный номер Заказа (ПИН-код, иные сведения)
                при получении Товаров, это расценивается как передача Покупателем такому лицу всех полномочий
                по получению, проверке (и оплате, если это применимо) Товаров.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.12.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Стоимость доставки Товаров рассчитывается автоматически. При определении стоимости доставки
                учитываются вес (в том числе объемный вес) Товаров, адрес доставки, протяжённость маршрута,
                скорость, срок доставки, день недели, время суток и способ доставки, выбранный Покупателем
                при оформлении Заказа вид тарифа, проводимые Продавцом (или с его участием) в момент
                оформления Заказа маркетинговые акции. Стоимость доставки указывается на последнем этапе
                оформления Заказа.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.13.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Во избежание случаев мошенничества, а также для выполнения взятых на себя обязательств при
                вручении предоплаченных Товаров, лицо, осуществляющее доставку Заказа Покупателю, вправе
                затребовать документ, удостоверяющий личность Получателя, а также зафиксировать тип и номер
                предоставленного Получателем документа. {legalName} гарантирует конфиденциальность и защиту
                персональной информации Получателя.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.14.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                При получении Товаров Получатель должен в месте получения Товаров проверить внешний вид и
                упаковку Товаров, количество Товаров в Заказе, комплектность, ассортимент, целостность
                Товаров. Подпись Получателя в документах, подтверждающих получение Товаров (в том числе в
                электронных документах), а равно сообщение Получателем одноразового кода означает отсутствие
                претензий Покупателя к количеству, внешнему виду, ассортименту, целостности и комплектности
                Товара. При обнаружении несоответствий вышеуказанных характеристик Товара Заказу Покупатель
                вправе отказаться от Товара (см. Раздел 7 ниже). После приемки Товара Получателем Продавец не
                принимает претензий к Товару в отношении вышеуказанных характеристик Товара. Товар считается
                полученным Покупателем в момент его передачи Получателю. Принимая Товар и подписывая
                документ, подтверждающий получение Товаров, или сообщая одноразовый код, Получатель
                подтверждает надлежащее исполнение обязательств Продавцом по Заказу.
                <Typography
                  variant="s"
                  size="body_long"
                  as="p"
                  style={{ margin: 'var(--space-100) 0 0 0' }}
                  color="text-primary"
                >
                  При получении Товара в Постамате Службы доставки или Торгового центра &laquo;Лемана
                  ПРО&raquo;, Товар считается полученным Получателем в момент автоматического открытия дверцы
                  Постамата после ввода ПИН-кода. В некоторых случаях (в зависимости от модели Постамата)
                  может потребоваться проставление Получателем подписи на экране Постамата, что признается
                  Покупателем и Продавцом аналогом собственноручной подписи. При обнаружении несоответствий
                  вышеуказанных характеристик Товара, полученного в Постамате Заказу, Покупатель должен
                  незамедлительно сообщить об этом {legalName} через форму обратной связи на Сайте:{' '}
                  <a href="/company/obratnaya-svyaz/" className={link}>
                    https://lemanapro.ru/company/obratnaya-svyaz/
                  </a>{' '}
                  или по телефону:{' '}
                  <a href={phoneNumber} className={cx(link, number)}>
                    8 (800) 700-00-99
                  </a>
                  .
                </Typography>
                <Typography
                  variant="s"
                  size="body_long"
                  as="p"
                  style={{ margin: 'var(--space-100) 0 0 0' }}
                  color="text-primary"
                >
                  При получении Заказа в Торговом центре &laquo;{legalName}&raquo; либо при выборе
                  Покупателем доставки Товаров из ассортимента {legalName} транспортной компанией, Покупатель
                  вправе отказаться от всех или некоторых из заказанных Товаров, сообщив об этом
                  представителю
                  {legalName}. Для остальных способов передачи Товаров частичный отказ недоступен &mdash; при
                  доставке в Постамат, в Пункт выдачи заказов Службы доставки или курьером Службы доставки.
                </Typography>
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.15.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Риск случайной гибели или случайного повреждения Товара переходит к Покупателю с момента
                передачи Товаров Получателю или ввода им ПИН-кода в Постамате. Покупатель и Продавец признают
                допустимым доказательством факта передачи Товара Получателю сообщение Получателем ПИН-кода
                (иных сведений о Заказе) курьеру Службы доставки, отметку Покупателя (в том числе электронную
                отметку в электронной накладной или электронном маршрутном листе Службы доставки) о получении
                Товара, видеозапись из Пункта выдачи заказов, из Торгового центра &laquo;{legalName}&raquo; и
                с места установки Постамата.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.16.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Если Заказ не может быть вручен Покупателю в срок (в том числе был вручен с нарушением срока
                или не вручен вообще) по независящим от Продавца причинам (Покупатель просит перенести срок
                доставки Заказа, Покупателя нет на месте в условленное время, Покупателем неверно указан
                адрес доставки или номер телефона, препятствия, мешающие подъезду Службы доставки, проходу
                или выгрузке, Покупатель не явился за Заказом в Пункт выдачи заказов или не забрал Заказ в
                установленный срок из Постамата, из-за отсутствия или ненадлежащего состояния подъездных
                путей, из-за невозможности прохода/переноса товара к месту доставки, неоплаты парковки
                Покупателем, при отказе от доставленного товара по инициативе Покупателя/Получателя Товара
                без предъявления претензий к качеству товара, а также в связи с неполным/недостоверным
                предоставлением Клиентом информации о Получателе либо по причине форс-мажора и проч.), то это
                считается отказом Покупателя от договора купли-продажи и является основанием для отмены
                Заказа. Если неполученный Заказ был предоплачен, {legalName} возвращает Покупателю уплаченные
                последним денежные средства (за вычетом стоимости доставки) в порядке, предусмотренном
                настоящими Правилами.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                5.17
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В случае недоставки Заказа по вине Продавца, {legalName} возвращает Покупателю стоимость
                предоплаченного Покупателем Заказа и доставки в порядке, предусмотренном настоящими
                Правилами.
              </Typography>
            </li>
          </ol>
        </li>
        <li className={listItem}>
          <div className={orderedItem}>
            <Typography
              variant="m"
              size="body_accent"
              as="span"
              className={orderNumber}
              color="text-primary"
            >
              6.
            </Typography>
            <Typography variant="m" size="body_accent" className={paragraphHeading} color="text-primary">
              Оплата Товара
            </Typography>
          </div>
          <ol className={subOrderedList}>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                6.1.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Цена Товара указывается в Интернет-Магазине.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                6.2.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Цена Товара в Интернет-Магазине может быть изменена Продавцом. Цена Товаров и общая стоимость
                Заказа указывается на последнем этапе оформления Заказа и действительна на момент нажатия
                кнопки &laquo;Оформить заказ&raquo;/&laquo;Перейти к оплате&raquo;.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                6.3.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Способы оплаты Товара указаны в Интернет-Магазине. Согласованным сторонами способом оплаты
                считается способ, выбранный Покупателем из доступных способов оплаты при оформлении Заказа.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                6.4.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                При предоплате Товаров Заказ принимается в работу только после его оплаты Покупателем.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                6.5.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Авторизация операций по банковским картам осуществляется банком. Если у банка есть основания
                полагать, что операция носит мошеннический характер, то банк вправе отказать в осуществлении
                данной операции. Из-за подозрений о возможности различного рода неправомерного использования
                банковских карт при оплате, Заказы, оформленные в Интернет-Магазине с предоплатой, могут не
                пройти авторизацию Банком. В этом случае Заказ останется неоплаченным и будет автоматически
                отменен.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                6.6.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                При оплате в Интернет-Магазине Покупателю направляется электронный чек (или ссылка на
                скачивание электронного чека) по адресу электронной почты, указанному Покупателем при
                оформлении Заказа на Сайте.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                6.7.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Исполнение Заказа Покупателя обеспечивает одно из подразделений {legalName} или лицо,
                указанное в качестве Продавца на странице Товара в Интернет-Магазине. В силу особенности
                организации торговли дистанционным способом в Интернет-Магазине, цена одного и того же Товара
                в разных регионах может отличаться в зависимости от климатических, экономических,
                географических границ, территориальных особенностей регионов, и других объективных факторов,
                связанных, в первую очередь, с различными затратами Продавца на перевозку, организацию
                продажи и доставку Товара Покупателю.
              </Typography>
            </li>
          </ol>
        </li>
        <li className={listItem}>
          <div className={orderedItem}>
            <Typography
              variant="m"
              size="body_accent"
              as="span"
              className={orderNumber}
              color="text-primary"
            >
              7.
            </Typography>
            <Typography variant="m" size="body_accent" className={paragraphHeading} color="text-primary">
              Возврат Товара и денежных средств, уплаченных за Товар.
            </Typography>
          </div>
          <ol className={subOrderedList}>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.1.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Подробные условия возврата Товаров и денежных средств приведены на Сайте в разделе
                &laquo;Возврат товара&raquo;{' '}
                <a href="/service/vozvrat-tovara/" className={link}>
                  https://lemanapro.ru/service/vozvrat-tovara/
                </a>
                {/* */}, которые являются неотъемлемой частью настоящих Правил. Покупатель обязан отдать
                возврат любому приехавшему за ним лицу, которое назовет номер заказа, и подписать Акт
                приема-передачи. Товары, продающиеся комплектом, подлежат возврату только комплектом.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.2.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Возврат товара надлежащего качества.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.2.1.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Покупатель вправе отказаться от Товара надлежащего качества в любое время до его получения, а
                после получения Товара &mdash; в течение 100 (Сто) дней со дня его получения, не считая дня
                передачи Товара Покупателю. Для Покупателей-участников программы лояльности {legalName}
                предусмотрен увеличенный срок возврата Товара надлежащего качества &mdash; подробнее см. в
                Правилах программы лояльности. Возврат Товаров надлежащего качества осуществляется силами и
                за счет Покупателя.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.2.2.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Покупатель не вправе отказаться от Товара надлежащего качества, имеющего
                индивидуально-определенные свойства, если указанный Товар может быть использован
                исключительно приобретающим его Покупателем.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.2.3.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Возврат Товара надлежащего качества (в том числе технически сложного товара бытового
                назначения) возможен в случае, если указанный Товар не был в употреблении, сохранены его
                потребительские свойства и товарный вид, пломбы, фабричные ярлыки и т.д., а также документ,
                подтверждающий факт и условия покупки указанного Товара у Продавца. При обнаружении следов
                эксплуатации Товара, Продавец оставляют за собой право отказать в возврате денежных средств.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.3.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Возврат товара ненадлежащего качества
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.3.1.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Покупатель может возвратить Товар ненадлежащего качества Продавцу и потребовать возврата
                уплаченной денежной суммы в течение гарантийного срока, срока годности либо, если такой срок
                не установлен, в разумный срок, не превышающий двух лет со дня передачи Товара Покупателю с
                учетом положений п.6 ст.18 Закона РФ &laquo;О защите прав потребителей&raquo;.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.4.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Обмен Товара надлежащего качества или замена Товара ненадлежащего качества осуществляется
                путем возврата Товара с последующей аннуляцией Заказа, затем оформления нового Заказа. При
                этом аннуляция является техническим действием и не подразумевает отказ Продавца от исполнения
                договора. Денежные средства при аннуляции Заказа возвращаются Покупателю в полном объеме,
                и новый оформленный взамен Заказ подлежит оплате. Если на момент обращения Покупателя Товар
                (и аналогичный Товар) отсутствует в продаже у Продавца, Покупатель вправе отказаться
                от исполнения договора купли-продажи и потребовать возврата уплаченной за указанный Товар
                денежной суммы. {legalName} возвращает денежную сумму за возвращенный Товар в установленный
                законом срок.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.5.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Возврат денежных средств
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.5.1.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                При отказе Покупателя от Товара надлежащего качества, {legalName} возвращает Покупателю
                уплаченную за Товар денежную сумму, за исключением расходов на доставку от Покупателя
                возвращенного Товара (в размере стоимости доставки, оплаченной Покупателем при оформлении
                Заказа).
                <Typography
                  variant="s"
                  size="body_long"
                  as="p"
                  style={{ margin: 'var(--space-100) 0 0 0' }}
                  color="text-primary"
                >
                  В случае отказа Покупателя от Товара ненадлежащего качества, {legalName} вернет Покупателю
                  уплаченную за Заказ денежную сумму. По требованию Продавца и за его счет Покупатель должен
                  возвратить Товар с недостатками.
                </Typography>
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.5.2.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Денежные средства будут возвращены Покупателю в течение семи дней со дня предъявления
                соответствующего требования, а при необходимости проведения экспертизы - четырнадцати дней.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.5.3.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Расходы на возврат суммы, уплаченной Покупателем за товар ненадлежащего качества, несет
                {legalName}. В случаях отказа Покупателя от Товара по иным основаниям, расходы на возврат
                суммы, уплаченной Покупателем за товар, несет Покупатель.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.5.4.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Если Товар был оплачен наличными деньгами (по платежному поручению) , то возврат денежных
                средств осуществляется на счет Покупателя в банке (на банковскую карту). Для этого Покупателю
                необходимо в заявлении на возврат Товара указать банковские реквизиты своего счета, полный
                почтовый адрес (в том числе индекс), фамилию, имя и отчество получателя. Только по номеру
                банковской карты Покупателя или по номеру телефона Покупателя возврат денежных средств
                произвести невозможно. Покупатель обязан предоставить полностью банковские реквизиты.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.5.5.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В случае, если Товар был оплачен банковской картой, то возврат денежных средств Покупателю
                осуществляется на ту же банковскую карту, с которой была произведена оплата. Для возврата
                денежных средств при оплате картой необходимо:
                <ol className={dottedOrderedList}>
                  <li className={dottedLi}>
                    <Typography
                      variant="s"
                      size="body_long"
                      as="p"
                      className={subParagraph}
                      color="text-primary"
                    >
                      Позвонить по номеру{' '}
                      <a href={phoneNumber} className={cx(link, number)}>
                        8 (800) 700-00-99
                      </a>
                    </Typography>
                  </li>
                  <li className={dottedLi}>
                    <Typography
                      variant="s"
                      size="body_long"
                      as="p"
                      className={subParagraph}
                      color="text-primary"
                    >
                      Заполнить бланк-заявление на возврат
                    </Typography>
                  </li>
                  <li className={dottedLi}>
                    <Typography
                      variant="s"
                      size="body_long"
                      as="p"
                      className={subParagraph}
                      color="text-primary"
                    >
                      Передать его сотруднику Службы доставки вместе с Товаром.
                    </Typography>
                  </li>
                </ol>
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.5.6.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В случае, если Покупатель возвращает Товар в Торговом центре &laquo;{legalName}&raquo;, то (с
                согласия Покупателя) денежные средства могут быть возвращены на Карту Возврата {legalName}.
                Картой Возврата {legalName} можно оплатить Товары в любом Торговом центре &laquo;Лемана
                ПРО&raquo; в России. Сумма денег на Карте Возврата {legalName} равна стоимости Товаров,
                которые были возвращены Покупателем в Торговом центре &laquo;{legalName}&raquo;.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.5.7.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В случае возврата денежных средств на Карту Возврата {legalName}, деньги возвращаются в
                распоряжение Покупателя немедленно (в момент вручения ему Карты Возврата {legalName}). Во
                всех остальных случаях денежные средства будут перечислены в течение 7 (Семь) дней с момента
                получения {legalName} письменного заявления Покупателя. Срок поступления денежных средств на
                банковскую карту Покупателя зависит от условий обслуживания банка Покупателя.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.5.8.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                При невозможности возврата денег на банковскую карту, которой была произведена оплата,
                Покупатель предоставляет заявление с указанием платежных реквизитов для возврата денег и
                объяснением причин (с подтверждением справкой о закрытии счета и/или иным документом из
                банка), по которым невозможен возврат денег на карту, которой была произведена оплата.
                Возврат денег наличными при оплате банковской картой не производится.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                7.5.9.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Если Товар был оплачен банковской картой, но Подтверждение о готовности Товара к отгрузке не
                было направлено Покупателю, то захолдированные на счете Покупателя (и еще не поступившие на
                расчетный счет {legalName}) денежные средства банк Покупателя расхолдирует на счете
                Покупателя автоматически. В таком случае уведомление о возврате денег Покупателю банком не
                направляется.
              </Typography>
            </li>
          </ol>
        </li>
        <li className={subListItem}>
          <Typography variant="s" size="body_long" as="span" className={subOrderNumber} color="text-primary">
            7.6.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
            Порядок действий при нарушении Продавцом условия об ассортименте:
          </Typography>
        </li>
        <li className={subListItem}>
          <Typography variant="s" size="body_long" as="span" className={subOrderNumber} color="text-primary">
            7.6.1.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
            В случае обнаружения в Заказе Товара, не соответствующего заказанному ассортименту (т.н.
            &laquo;пересорт&raquo;) либо расхождений по количеству Товара, Покупатель вправе при доставке
            отказаться от Заказа полностью либо от части Заказа (если в Заказе только Товары {legalName}, а
            способ получения - доставка транспортной компанией), сделав об этом отметку в документе,
            подтверждающем получение (доставку) Заказа.
          </Typography>
        </li>
        <li className={subListItem}>
          <Typography variant="s" size="body_long" as="span" className={subOrderNumber} color="text-primary">
            7.6.2.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
            Денежные средства, оплаченные Покупателем за фактически непереданный Товар, подлежат возврату на
            основании обращения Покупателя с заявлением о возврате денежных средств. Возврат уплаченной за
            Товар суммы осуществляется тем способом, которым была произведена оплата.
          </Typography>
        </li>
        <li className={subListItem}>
          <Typography variant="s" size="body_long" as="span" className={subOrderNumber} color="text-primary">
            7.6.3.
          </Typography>
          <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
            Товар, переданный Покупателю в нарушение условия об ассортименте/количестве, подлежит возврату
            Продавцу. Если Покупатель все же принимает данный Товар, то {legalName} вправе потребовать от
            Покупателя оплаты данного Товара по цене, установленной для данного Товара в Интернет-Магазине на
            момент передачи Товара. Если фактически переданный Товар отсутствует в ассортименте
            Интернет-Магазина на момент передачи Товара, данный Товар оплачивается по цене, согласованной с
            {legalName}.
          </Typography>
        </li>
        <li className={listItem}>
          <div className={orderedItem}>
            <Typography
              variant="m"
              size="body_accent"
              as="span"
              className={orderNumber}
              color="text-primary"
            >
              8.
            </Typography>
            <Typography variant="m" size="body_accent" className={paragraphHeading} color="text-primary">
              Интеллектуальные права
            </Typography>
          </div>
          <ol className={subOrderedList}>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                8.1.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Интеллектуальные права на материалы, представленные на Сайте и в Мобильном приложении &laquo;
                {legalName}&raquo;, включая все его содержимое, информацию, фотографии, иллюстрации, рисунки,
                наименования, товарные знаки, коммерческие обозначения и логотипы, принадлежат Продавцу и/или
                его поставщикам, партнерам и производителям Товаров, и охраняются законом. Материалы
                предназначены исключительно для личного, некоммерческого использования. Содержание Сайта, как
                описано выше, не может быть изменено, скопировано, воспроизведено, или выставлено на продажу,
                сдано в аренду, дополнено или использовано любым другим способом без предварительного
                письменного разрешения от ООО &laquo;ЛЕ МОНЛИД&raquo;.
              </Typography>
            </li>
          </ol>
        </li>
        <li className={listItem}>
          <div className={orderedItem}>
            <Typography
              variant="m"
              size="body_accent"
              as="span"
              className={orderNumber}
              color="text-primary"
            >
              9.
            </Typography>
            <Typography variant="m" size="body_accent" className={paragraphHeading} color="text-primary">
              Гарантии и ответственность
            </Typography>
          </div>
          <ol className={subOrderedList}>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                9.1.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Продавец не несет ответственности за ущерб, причиненный Покупателю вследствие ненадлежащего
                использования Товаров, заказанных в Интернет-Магазине.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                9.2.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                {legalName} не несет ответственности за содержание и функционирование внешних сайтов, ссылки
                на которые могут содержаться в Интернет-Магазине.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                9.3.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                {legalName} вправе без предварительного уведомления Покупателя переуступать либо каким-либо
                иным способом передавать свои права и обязанности, вытекающие из его отношений с Покупателем,
                третьим лицам.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                9.4.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Информация о форме и способах направления претензий. Свои вопросы и пожелания Покупатель
                может направить в Интернет-Магазине через форму обратной связи, размещенную в
                Интернет-Магазине на странице &laquo;Клиентская поддержка&raquo; по адресу{' '}
                <a href="/company/obratnaya-svyaz/" className={link}>
                  https://lemanapro.ru/company/obratnaya-svyaz/
                </a>
                , указав соответствующую Тему обращения, или по телефону{' '}
                <a href={phoneNumber} className={cx(link, number)}>
                  8 (800) 700-00-99
                </a>
                . В случае возникновения претензий со стороны Покупателя, он может обратиться к Продавцу
                через ту же форму обратной связи, указав Тему обращения – «Претензия» или по телефону{' '}
                <a href={phoneNumber} className={cx(link, number)}>
                  8 (800) 700-00-99
                </a>
                . Все возникающее споры стороны будут рассматриваться в порядке, предусмотренном действующим
                законодательством РФ.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                9.5.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Признание судом недействительности какого-либо положения настоящих Правил не влечет за собой
                недействительность остальных положений.
              </Typography>
            </li>
          </ol>
        </li>
        <li>
          <div className={orderedItem}>
            <Typography
              variant="m"
              size="body_accent"
              as="span"
              className={orderNumber}
              color="text-primary"
            >
              10.
            </Typography>
            <Typography variant="m" size="body_accent" color="text-primary" className={paragraphHeading}>
              Конфиденциальность и защита персональной информации
            </Typography>
          </div>
          <ol className={subOrderedList}>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                10.1.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Предоставляя свои персональные данные при оформлении Заказа, Покупатель подтверждает, что
                уведомлен о том, что {legalName} будет осуществлять обработку его персональных данных
                (персональных данных Получателя) с целью исполнения Заказа, а также то, что {legalName}{' '}
                вправе передавать персональные данные Покупателя (Получателя) третьим лицам, включая
                Продавцов и Службы доставки, для целей исполнения обязательств {legalName}/Продавцов перед
                Покупателем. Подробно описание принципов и подходов {legalName} в отношении обработки и
                обеспечения безопасности персональных данных Покупателей приведено в Политике обработки
                персональных данных в ООО &laquo;ЛЕ МОНЛИД&raquo;, размещенной на Сайте.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                10.2.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Если Покупатель выразил свое согласие, то {legalName} вправе направлять ему сообщения
                рекламного характера (в форме СМС и/или пуш-уведомлений, и/или посредством приложений, и/или
                мессенджеров для смартфонов, и/или телефонных звонков, и/или по электронной почте, и/или иным
                образом на указанный Покупателем номер телефона и/или адрес электронной почты).
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                10.3.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                {legalName} вправе направлять Покупателю сообщения информационного (сервисного) характера.
                Оформляя Заказ или авторизуясь в Интернет-Магазине, Покупатель соглашается с получением
                сообщений сервисного характера одновременно или по отдельности на адрес электронной почты
                (если укажет его Личном кабинете) / посредством смс-сообщений / пуш-уведомлений / приложений
                / мессенджеров / иным образом на номер телефона, указанный Покупателем при регистрации и/или
                оформлении Заказа, о состоянии Заказа, условиях доставки, Товарах в корзине Покупателя и/или
                добавленных Покупателем в &laquo;Список покупок&raquo;, о накопленных бонусах (баллах), о
                появлении указанного Покупателем Товара в наличии, о предоставлении промокода на скидку и
                порядка его использования, о грядущем сгорании бонусов (баллов) или скидках на любимый Товар,
                выбранный Покупателем, информацию о персональных (индивидуальных) бонусах (баллах),
                промокодах Покупателя и др.), а также с просьбой оставить отзыв. Отказ Покупателя от
                получения указанных сообщений невозможен по техническим причинам.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                10.4.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Если Покупатель не желает получать рассылки от {legalName}, он должен отказаться от подписки
                путём &laquo;клика&raquo; по соответствующей ссылке в направляемом {legalName} письме и/или
                направления обращения через форму обратной связи на Сайте по адресу{' '}
                <a href="/company/obratnaya-svyaz/" className={link}>
                  https://lemanapro.ru/company/obratnaya-svyaz/
                </a>{' '}
                либо путем его направления посредством почтовой связи по адресу: 115280, г. Москва, пр.
                Лихачёва, д. 15, помещ. 1/1, ООО &laquo;ЛЕ МОНЛИД&raquo;.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                10.5.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                {legalName} использует технологию &laquo;cookies&raquo;. Подробная информация о технологии
                &laquo;cookies&raquo; и её использовании {legalName} содержится в Предупреждении об
                использовании файлов cookies на Сайте.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                10.6.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                {legalName} получает информацию об IP-адресе посетителя Сайта. Данная информация не
                используется для установления личности посетителя Сайта.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                10.7.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                {legalName} не несет ответственности за сведения, сообщенные Покупателем на Сайте в
                общедоступной форме.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                10.8.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                {legalName} не несет ответственности за убытки, которые Покупатель может понести в результате
                того, что его логин и пароль стали известны третьему лицу.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                10.9.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                В случае возникновения у {legalName} подозрений относительно использования Личного кабинета,
                логина, пароля, адреса электронной почты Покупателя третьим лицом или вредоносным программным
                обеспечением, {legalName} вправе в одностороннем порядке изменить пароль Покупателя
                с уведомлением последнего на указанный при регистрации адрес электронной почты.
              </Typography>
            </li>
            <li className={subListItem}>
              <Typography
                variant="s"
                size="body_long"
                as="span"
                className={subOrderNumber}
                color="text-primary"
              >
                10.10.
              </Typography>
              <Typography variant="s" size="body_long" as="p" className={subParagraph} color="text-primary">
                Правила разработаны в соответствии с положениями Декрета Высшего Государственного Совета
                Союзного государства от 6 декабря 2024 г. № 6 &laquo;О единых правилах в области защиты прав
                потребителей&raquo;, ГК РФ о розничной купле-продаже (§ 2 глава 30), Законом РФ от 07.02.1992
                № 2300-1 &laquo;О защите прав потребителей&raquo; и Постановлением Правительства
                РФ от 31.12.2020 № 2463 &laquo;Об утверждении Правил продажи товаров по договору розничной
                купли-продажи...&raquo;.
              </Typography>
            </li>
          </ol>
        </li>
      </OrderedList>
    </div>
  </>
));
